export type GatewayFinalizationCamera = {
  cameraId?: string;
  SK?: string;
  onboardingStatus?: string;
  onboardingError?: string;
};

type ReadyResult = {
  state: "ready";
  camera: GatewayFinalizationCamera;
};

type PendingResult = {
  state: "pending";
  reason: string;
  retryAfterSeconds: number;
  message: string;
};

export type GatewayFinalizationResult = ReadyResult | PendingResult;

function boundedRetrySeconds(value: unknown) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 15;
  return Math.min(60, Math.max(5, Math.floor(parsed)));
}

export function classifyGatewayFinalizationResponse(
  status: number,
  body: any,
): GatewayFinalizationResult {
  if (status === 202) {
    if (body?.placementPending !== true) {
      throw new Error("Gateway finalizer returned an invalid accepted response.");
    }
    return {
      state: "pending",
      reason: String(
        body?.reason ||
          body?.code ||
          "NO_SAFE_GATEWAY_FOR_NEW_CAMERA",
      ),
      retryAfterSeconds: boundedRetrySeconds(body?.retryAfterSeconds),
      message: String(
        body?.message ||
          "AWS Media Gateway capacity is starting. Camera setup will continue automatically.",
      ),
    };
  }

  if (status === 200) {
    const camera = body?.camera as GatewayFinalizationCamera | undefined;
    if (!camera || camera.onboardingStatus !== "READY") {
      throw new Error("Gateway finalizer did not return a READY camera.");
    }
    return { state: "ready", camera };
  }

  throw new Error(`Gateway finalizer returned unexpected status ${status}.`);
}

export function findGatewayCamera(
  payload: any,
  cameraId: string,
): GatewayFinalizationCamera | null {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  return (
    items.find((item: GatewayFinalizationCamera) => {
      const id = String(
        item?.cameraId || String(item?.SK || "").replace(/^CAMERA#/, ""),
      ).trim();
      return id === String(cameraId);
    }) || null
  );
}

export function gatewayFinalizationFailure(camera: GatewayFinalizationCamera) {
  const status = String(camera?.onboardingStatus || "").toUpperCase();
  if (!["FINALIZE_ERROR", "FAILED"].includes(status)) return null;
  return String(
    camera?.onboardingError ||
      "Gateway camera setup failed. Retry after checking the Gateway and camera.",
  );
}
